import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Page, PageType } from "domain/entities/Page";
import pageRepository from "infra/repositories/website-repository";

const pagesKey = (type: PageType) => ["pages", type] as const;

export function usePages(type: PageType) {
  return useQuery<Page[]>({
    queryKey: pagesKey(type),
    queryFn: async () => {
      const res = await pageRepository.getAll(type);
      return res.data ?? [];
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

export function useCreatePage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; type: PageType }) =>
      pageRepository.create(input.name, input.type),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: pagesKey(variables.type) });
    },
  });
}

export function useRenamePage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; name: string }) =>
      pageRepository.rename(input.id, input.name),
    onSuccess: (_data, _vars, context: { type: PageType } | undefined) => {
      if (context?.type) {
        qc.invalidateQueries({ queryKey: pagesKey(context.type) });
      } else {
        qc.invalidateQueries({ queryKey: ["pages"] });
      }
    },
  });
}

export function useUpdatePage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; name?: string; image?: string; description?: string }) =>
      pageRepository.update(input.id, { name: input.name, image: input.image, description: input.description }),
    onSuccess: (_data, _vars, context: { type: PageType } | undefined) => {
      if (context?.type) {
        qc.invalidateQueries({ queryKey: pagesKey(context.type) });
      } else {
        qc.invalidateQueries({ queryKey: ["pages"] });
      }
    },
  });
}

export function useDeletePage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string }) => pageRepository.remove(input.id),
    onSuccess: (_data, _vars, context: { type: PageType } | undefined) => {
      if (context?.type) {
        qc.invalidateQueries({ queryKey: pagesKey(context.type) });
      } else {
        qc.invalidateQueries({ queryKey: ["pages"] });
      }
    },
  });
}
